import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ArrowRight, TrendingUp, TrendingDown, Minus, Loader2, BarChart3, Car, CalendarRange, Target } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, EmptyState, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mustauto/ui/web';
import { useMarketIntelligence, useOpportunities } from '@/hooks/useMarketIntelligence';
import { useTier } from '@/hooks/useTier';
import { TierLockedOverlay } from '@/components/TierLockedOverlay';
import { TierUpgradeModal } from '@/components/TierUpgradeModal';
import { formatReais, formatNumber } from '@/lib/utils';

const TREND_CONFIG = {
  up: { icon: TrendingUp, label: 'Crescendo', variant: 'success' as const, color: 'var(--ma-success)' },
  stable: { icon: Minus, label: 'Estável', variant: 'secondary' as const, color: 'var(--ma-steel-600)' },
  down: { icon: TrendingDown, label: 'Caindo', variant: 'warning' as const, color: 'var(--ma-warning)' },
};

const PRIORITY_CONFIG = {
  red: { label: 'Sem concorrência', variant: 'destructive' as const, emoji: '🔴' },
  orange: { label: 'Expansão', variant: 'warning' as const, emoji: '🟠' },
  yellow: { label: 'Em alta', variant: 'info' as const, emoji: '🟡' },
};

/**
 * Série categórica. Antes era uma escala de teal e ciano — que virou a cor de
 * interação da marca, e barra turquesa passa a ler como "clicável". Aqui as
 * matizes são distintas entre si e todas fora da faixa da turquesa (177°).
 */
const BAR_COLORS = [
  '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', // ux-lint-allow-hex
  '#ec4899', '#84cc16', '#f97316', '#0ea5e9', '#a16207', // ux-lint-allow-hex
];

// Build monthly heatmap data for seasonality
function buildSeasonalityGrid(
  data: { month: string; category: string; osCount: number }[],
): { months: string[]; categories: string[]; grid: Map<string, number>; max: number } {
  const monthSet = new Set<string>();
  const categorySet = new Set<string>();
  const grid = new Map<string, number>();
  let max = 0;

  for (const row of data) {
    monthSet.add(row.month);
    categorySet.add(row.category);
    const key = `${row.month}:${row.category}`;
    grid.set(key, row.osCount);
    if (row.osCount > max) max = row.osCount;
  }

  const months = Array.from(monthSet).sort();
  const categories = Array.from(categorySet);

  return { months, categories, grid, max };
}

function heatColor(value: number, max: number): string {
  if (max === 0) return 'var(--color-muted)';
  const ratio = value / max;
  // Escala sequencial (intensidade, não categoria) — aqui a rampa da marca é o
  // uso certo: um só matiz, do mais fraco ao mais forte.
  if (ratio > 0.7) return 'var(--ma-teal-700)';
  if (ratio > 0.4) return 'var(--ma-teal-500)';
  if (ratio > 0.15) return 'var(--ma-teal-300)';
  if (ratio > 0) return 'var(--ma-teal-100)';
  return 'var(--color-muted)';
}

function monthLabel(yyyymm: string): string {
  const [, m] = yyyymm.split('-');
  const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return labels[Number(m) - 1] ?? m;
}

export function MarketIntelligencePage() {
  const { tierInfo, canAccess, tierUnknown, tierLoading, refetchTier } = useTier();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const hasAccess = canAccess('marketIntelligence');
  const {
    data: market,
    isLoading: loadingMarket,
    isError: marketError,
    error: marketErrorObj,
    refetch: refetchMarket,
  } = useMarketIntelligence();
  const { data: opps, isLoading: loadingOpps, isError: oppsError, refetch: refetchOpps } = useOpportunities();

  // Cadeado so quando SABEMOS que o plano nao inclui. Falhou a busca do
  // tier, a tela diz isso — nao oferece upgrade para o tier ja pago.
  if (tierUnknown) {
    return (
      <ErrorState
        title="Não foi possível verificar seu plano"
        detail="Sem isso não dá para saber se a inteligência de mercado está incluída no seu plano."
        onRetry={() => void refetchTier()}
      />
    );
  }

  // Carregando nao e bloqueado: sem isto a tela mostrava o cadeado e o
  // convite de upgrade no intervalo entre abrir a pagina e o dashboard
  // responder — o mesmo que o sidebar deixou de fazer com `tierKnown`.
  if (tierLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold">Inteligência de Mercado</h1>
          <p className="text-sm text-muted-foreground">
            Dados agregados e anônimos de todas as oficinas — descubra onde investir
          </p>
        </div>
        <TierLockedOverlay
          requiredTier="silver"
          featureLabel="Inteligência de Mercado"
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />
        {showUpgradeModal && tierInfo && (
          <TierUpgradeModal currentTier={tierInfo.current} onClose={() => setShowUpgradeModal(false)} />
        )}
      </div>
    );
  }

  if (loadingMarket || loadingOpps) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Cinco `?? []` alimentam esta tela. Com a busca falhada ela desenhava
  // graficos de demanda zerada e um heatmap todo apagado — que o parceiro le
  // como "nao ha demanda nessa categoria", exatamente ao contrario do que a
  // falha significa. Sem `market` nao ha tela; e tudo ou nada.
  if (marketError && !market) {
    return (
      <ErrorState
        title="Não foi possível carregar a inteligência de mercado"
        error={marketErrorObj}
        onRetry={() => {
          void refetchMarket();
          void refetchOpps();
        }}
      />
    );
  }

  const topCategories = market?.topCategories ?? [];
  const topVehicles = market?.topVehicles ?? [];
  const coverageGaps = market?.coverageGaps ?? [];
  const seasonality = market?.seasonality ?? [];
  const opportunities = opps?.opportunities ?? [];

  const seasonalGrid = buildSeasonalityGrid(seasonality);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Inteligência de Mercado</h1>
        <p className="text-sm text-muted-foreground">
          Dados agregados e anônimos de todas as oficinas — descubra onde investir
        </p>
      </div>

      {/* ── Section 1: Demanda por Categoria ── */}
      <Card>
        <CardHeader>
          <CardTitle>Demanda por Categoria</CardTitle>
          <p className="text-sm text-muted-foreground">
            Top categorias por volume de OS nos últimos 30 dias
          </p>
        </CardHeader>
        <CardContent>
          {topCategories.length === 0 ? (
            <EmptyState
              compact
              icon={<BarChart3 size={26} />}
              title="Ainda não há dados de demanda"
              description="Assim que as oficinas registrarem OS nesta categoria, o ranking aparece aqui."
            />
          ) : (
            <div className="space-y-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topCategories}
                    layout="vertical"
                    margin={{ left: 20, right: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      type="category"
                      dataKey="category"
                      width={120}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value) => [formatNumber(Number(value ?? 0)), 'OS']}
                      contentStyle={{ borderRadius: 8 }}
                    />
                    <Bar dataKey="totalOsCount" radius={[0, 4, 4, 0]}>
                      {topCategories.map((_, i) => (
                        <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-right">OS</TableHead>
                    <TableHead className="text-right">Ticket médio</TableHead>
                    <TableHead>Tendência</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topCategories.map((cat) => {
                    const t = TREND_CONFIG[cat.trend];
                    const Icon = t.icon;
                    return (
                      <TableRow key={cat.category}>
                        <TableCell className="font-medium">{cat.category}</TableCell>
                        <TableCell className="text-right">{formatNumber(cat.totalOsCount)}</TableCell>
                        <TableCell className="text-right">{formatReais(cat.avgTicket)}</TableCell>
                        <TableCell>
                          <Badge variant={t.variant} className="gap-1">
                            <Icon size={12} />
                            {t.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Section 2: Veículos mais atendidos ── */}
      <Card>
        <CardHeader>
          <CardTitle>Veículos Mais Atendidos</CardTitle>
          <p className="text-sm text-muted-foreground">
            Ranking de modelos com maior volume de OS
          </p>
        </CardHeader>
        <CardContent>
          {topVehicles.length === 0 ? (
            <EmptyState
              compact
              icon={<Car size={26} />}
              title="Ainda não há dados por modelo"
              description="O ranking de modelos precisa de um volume mínimo de OS para não induzir a erro."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Marca</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead className="text-right">OS</TableHead>
                  <TableHead className="text-right">% do total</TableHead>
                  <TableHead className="w-48">Distribuição</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topVehicles.map((v, i) => (
                  <TableRow key={`${v.make}-${v.model}`}>
                    <TableCell className="font-bold text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-medium">{v.make}</TableCell>
                    <TableCell>{v.model}</TableCell>
                    <TableCell className="text-right">{formatNumber(v.osCount)}</TableCell>
                    <TableCell className="text-right">{v.percentage}%</TableCell>
                    <TableCell>
                      <div className="h-2 w-full rounded-full bg-muted">
                        <div
                          className="h-2 rounded-full bg-primary"
                          style={{ width: `${Math.min(v.percentage * 3, 100)}%` }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Section 3: Oportunidades ── */}
      <div>
        <h2 className="mb-4 text-lg font-bold">Oportunidades</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Categorias com alta demanda e nenhum parceiro ativo — 100% da demanda disponível
        </p>
        {oppsError && opportunities.length === 0 && coverageGaps.length === 0 ? (
          /* Sem este ramo, a busca falhada de oportunidades dizia "as
             categorias da sua regiao ja tem cobertura" — a conclusao oposta,
             e a que faz o parceiro NAO investir. */
          <Card>
            <CardContent className="p-0">
              <ErrorState
                title="Não foi possível carregar as lacunas de cobertura"
                onRetry={() => void refetchOpps()}
              />
            </CardContent>
          </Card>
        ) : opportunities.length === 0 && coverageGaps.length === 0 ? (
          <Card>
            <CardContent className="p-0">
              <EmptyState
                compact
                icon={<Target size={26} />}
                title="Nenhuma lacuna aberta no momento"
                description="As categorias da sua região já têm cobertura. Voltamos a listar aqui assim que surgir demanda descoberta."
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {opportunities.map((opp) => {
              const p = PRIORITY_CONFIG[opp.priority];
              return (
                <Card key={opp.category}>
                  <CardHeader className="flex flex-row items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span>{p.emoji}</span>
                        <CardTitle className="text-base font-bold">
                          {opp.category}
                        </CardTitle>
                        <Badge variant={p.variant}>{p.label}</Badge>
                      </div>
                    </div>
                    <Link
                      to="/campaigns/new"
                      search={{ category: opp.category }}
                      className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                    >
                      Criar campanha
                      <ArrowRight size={12} />
                    </Link>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground">Demanda mensal</div>
                        <div className="text-lg font-bold">{opp.osCount} OS/mês</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Ticket médio</div>
                        <div className="text-lg font-bold">{formatReais(opp.avgTicket)}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Orçamento sugerido</div>
                        <div className="text-lg font-bold">{formatReais(opp.suggestedBudget)}/mês</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Tendência</div>
                        <Badge variant={TREND_CONFIG[opp.trend].variant} className="mt-1 gap-1">
                          {(() => { const I = TREND_CONFIG[opp.trend].icon; return <I size={12} />; })()}
                          {TREND_CONFIG[opp.trend].label}
                        </Badge>
                      </div>
                    </div>

                    {opp.topModels.length > 0 && (
                      <div className="mt-4">
                        <div className="text-xs font-medium text-muted-foreground">Principais modelos</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {opp.topModels.map((model) => (
                            <Badge key={model} variant="outline">{model}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Section 4: Sazonalidade ── */}
      <Card>
        <CardHeader>
          <CardTitle>Sazonalidade</CardTitle>
          <p className="text-sm text-muted-foreground">
            Volume de OS por categoria ao longo dos últimos 12 meses — planeje seu budget sazonal
          </p>
        </CardHeader>
        <CardContent>
          {seasonalGrid.months.length === 0 ? (
            <EmptyState
              compact
              icon={<CalendarRange size={26} />}
              title="Ainda não há histórico sazonal"
              description="A sazonalidade compara 12 meses — ela aparece quando houver esse histórico."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="p-2 text-left font-medium text-muted-foreground">Categoria</th>
                    {seasonalGrid.months.map((m) => (
                      <th key={m} className="p-2 text-center font-medium text-muted-foreground">
                        {monthLabel(m)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {seasonalGrid.categories.map((cat) => (
                    <tr key={cat}>
                      <td className="p-2 font-medium whitespace-nowrap">{cat}</td>
                      {seasonalGrid.months.map((m) => {
                        const val = seasonalGrid.grid.get(`${m}:${cat}`) ?? 0;
                        return (
                          <td key={m} className="p-1 text-center">
                            <div
                              className="mx-auto flex h-8 w-10 items-center justify-center rounded text-[10px] font-semibold"
                              style={{
                                backgroundColor: heatColor(val, seasonalGrid.max),
                                // A partir de teal-500 o fundo já é escuro o
                                // bastante para exigir texto claro.
                                color:
                                  val / seasonalGrid.max > 0.4
                                    ? 'var(--ma-paper)'
                                    : 'var(--ma-steel-800)',
                              }}
                              title={`${cat} — ${monthLabel(m)}: ${val} OS`}
                            >
                              {val || ''}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Menos</span>
                {['var(--color-muted)', 'var(--ma-teal-100)', 'var(--ma-teal-300)', 'var(--ma-teal-500)', 'var(--ma-teal-700)'].map((c) => (
                  <div key={c} className="h-4 w-6 rounded" style={{ backgroundColor: c }} />
                ))}
                <span>Mais</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
